/**
 * TanStack Query hooks for per-user notification preferences (AB#570 backend).
 * Shared by the mobile prefs screen (AB#571) and the wpp-admin settings route
 * (AB#572) — one fetch/mutation impl, no duplication.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationPrefsApi } from '../api/notificationPrefs';
import type {
  NotificationPrefs,
  NotificationPrefsInput,
} from '../api/notificationPrefs';
import type { ApiResponse } from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const notificationPrefsKeys = {
  all: ['notification-prefs'] as const,
};

type PrefsResponse = ApiResponse<{ prefs: NotificationPrefs }>;

// ============================================================================
// Hooks
// ============================================================================

/** Fetch the effective notification matrix (all 10 events with current values). */
export function useNotificationPrefs() {
  return useQuery({
    queryKey: notificationPrefsKeys.all,
    queryFn: () => notificationPrefsApi.get(),
  });
}

/**
 * Persist changed events via PUT with an optimistic overlay + rollback. The
 * optimistic cache merges the sent events over the current matrix; the server
 * response (which re-applies defaults + security locks) then replaces it, so a
 * dropped/ignored value self-corrects on success.
 */
export function useUpdateNotificationPrefs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prefs: NotificationPrefsInput) => notificationPrefsApi.put(prefs),
    onMutate: async (prefs: NotificationPrefsInput) => {
      await queryClient.cancelQueries({ queryKey: notificationPrefsKeys.all });
      const previous = queryClient.getQueryData<PrefsResponse>(notificationPrefsKeys.all);
      if (previous?.data?.prefs) {
        queryClient.setQueryData<PrefsResponse>(notificationPrefsKeys.all, {
          ...previous,
          data: { prefs: { ...previous.data.prefs, ...prefs } },
        });
      }
      return { previous };
    },
    onError: (_err, _prefs, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationPrefsKeys.all, context.previous);
      }
    },
    onSuccess: (response) => {
      // Server is authoritative — replace with the returned effective matrix.
      queryClient.setQueryData(notificationPrefsKeys.all, response);
    },
  });
}
